'use client'

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { getErrorStatus } from '@/service/shared-layout.hook'

const MAX_QUERY_RETRIES = 2
let lastTransientWarning = -Infinity

function reportTransientError(error: unknown): boolean {
  if (!(error as { transient?: boolean })?.transient) return false
  if (Date.now() - lastTransientWarning >= 30_000) {
    lastTransientWarning = Date.now()
    console.warn('[query] Connection unavailable; session retained')
  }
  return true
}

// A 4xx is the server rejecting the request itself (bad params, auth already handled by the
// axios interceptor, not found) and fails the same way every time. Anything else — a 5xx, a
// 503 from a transient token-refresh failure, a dropped connection with no status — gets up
// to two more attempts with the library's exponential backoff (1s, 2s). 429s never reach
// here un-retried: the axios interceptor already backs off on those.
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = getErrorStatus(error)
  if (status !== undefined && status >= 400 && status < 500) return false
  return failureCount < MAX_QUERY_RETRIES
}

// refetchOnWindowFocus stays at the library default (on): with a 5s staleTime it re-reads
// server state after the tab was in the background, which the live streams don't always
// backfill, and every query that must not refetch already opts out itself.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!reportTransientError(error)) console.error('[query] failed', query.queryKey, error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (reportTransientError(error)) return
      // Every call posts to one endpoint, so name the operation and the server's own reason.
      const op = isAxiosError(error) ? error.config?._op : undefined
      const reason = isAxiosError(error) ? error.response?.data : undefined
      console.error(
        '[mutation] failed',
        mutation.options.mutationKey ?? op ?? '(no key)',
        ...(reason ? [reason] : []),
        error
      )
    },
  }),
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      staleTime: 5000,
    },
  },
})

export default function _QueryProvider({ children }: { children: React.ReactNode }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
